<script setup lang="ts">
/* Developer overlay — gated behind prefs.dev. A scratch surface for things only
   developers poke at; today it hosts the sound soundboard (audition every cue
   without having to provoke the real event). Add new tools as their own section. */
import Icon from '@/components/Icon.vue'
import { useUi } from '@/stores/ui'
import { usePrefs } from '@/stores/prefs'
import { SFX_CATALOG, previewSfx, type Sfx } from '@/lib/sfx'

const ui = useUi()
const prefs = usePrefs()

function play(name: Sfx) { previewSfx(name) }
</script>

<template>
  <div class="dev-back" @click="ui.closeDev()" />
  <div class="dev-panel in" role="dialog" aria-label="Developer panel">
    <div class="dev-head">
      <div class="row gap-2"><Icon name="waveform" :size="18" /><b>Dev panel</b></div>
      <button class="icon-mini" @click="ui.closeDev()"><Icon name="x" :size="16" /></button>
    </div>

    <div class="dev-body">
      <section class="dev-sec">
        <div class="dev-sec__h">
          <Icon name="sound" :size="15" /><span>Soundboard</span>
          <span class="dev-note">
            {{ prefs.sfx ? 'cues on' : 'cues off — preview still plays' }} · vol {{ Math.round(prefs.sfxVolume * 100) }}%
          </span>
        </div>
        <div v-for="g in SFX_CATALOG" :key="g.group" class="sb-group">
          <div class="sb-group__label">{{ g.group }}</div>
          <div class="sb-grid">
            <button v-for="it in g.items" :key="it.name" class="sb-btn" :title="it.desc" @click="play(it.name)">
              <span class="sb-btn__play"><Icon name="sound" :size="13" /></span>
              <span class="sb-btn__txt"><b>{{ it.label }}</b><small>{{ it.desc }}</small></span>
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.dev-back { position: fixed; inset: 0; z-index: 219; background: color-mix(in srgb, var(--bg-0, #000) 40%, transparent); }
.dev-panel {
  position: fixed; top: 50%; left: 50%; z-index: 220;
  width: 460px; max-width: calc(100vw - 32px); max-height: calc(100vh - 80px);
  display: flex; flex-direction: column; overflow: hidden;
  background: var(--surface-1); border: 1px solid var(--line-strong); border-radius: var(--r-lg);
  box-shadow: var(--sh-pop);
  transform: translate(-50%, -50%) scale(.97); opacity: 0;
  transition: transform var(--t-med) var(--ease-out), opacity var(--t-med) var(--ease-out);
}
.dev-panel.in { transform: translate(-50%, -50%) scale(1); opacity: 1; }
.dev-head { display: flex; align-items: center; justify-content: space-between; padding: 13px 14px; border-bottom: 1px solid var(--line); }
.dev-head b { font-family: var(--font-display); font-size: 15px; font-weight: 500; }
.dev-head .ico { color: var(--gold-bright); }
.dev-body { overflow-y: auto; padding: 14px; }
.dev-sec__h { display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 700; color: var(--text-mid); text-transform: uppercase; letter-spacing: .04em; margin-bottom: 12px; }
.dev-sec__h .ico { color: var(--gold-bright); }
.dev-note { margin-left: auto; font-weight: 500; text-transform: none; letter-spacing: 0; color: var(--text-faint); font-size: 11px; }
.sb-group { margin-bottom: 12px; }
.sb-group__label { font-size: 11px; color: var(--text-faint); margin: 0 0 6px 2px; }
.sb-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
.sb-btn { display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: var(--r-md); background: var(--surface-2); border: 1px solid var(--line); text-align: left; transition: all var(--t-fast); }
.sb-btn:hover { background: var(--surface-3); border-color: var(--line-strong); }
.sb-btn:active { transform: scale(.97); }
.sb-btn__play { width: 26px; height: 26px; border-radius: 8px; flex: none; display: grid; place-items: center; background: var(--gold); color: var(--gold-ink); }
.sb-btn__txt { display: flex; flex-direction: column; min-width: 0; }
.sb-btn__txt b { font-size: 12.5px; font-weight: 700; color: var(--text-hi); }
.sb-btn__txt small { font-size: 10.5px; color: var(--text-lo); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
